import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Award, Calendar, ChevronRight, Phone, Building2, Briefcase, Shield, Lightbulb, Heart } from 'lucide-react';
import { companyInfo, stats, values, benefits, upcomingEvents, newsArticles } from '../data/mock';
import Layout from '../components/layout/Layout';

const HomePage = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-[#0A1628]">
        {/* Background Pattern */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#0F2847] to-[#0A1628]" />
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500 rounded-full blur-3xl" />
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-400 rounded-full blur-3xl" />
          </div>
          {/* Grid pattern */}
          <div className="absolute inset-0 opacity-5" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-blue-300 text-sm mb-6">
                <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
                Centro Empresarial Manzanillo
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                Nadie es mejor que{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-200">
                  todos juntos
                </span>
              </h1>
              <p className="text-lg text-gray-300 mb-8 leading-relaxed max-w-xl">
                {companyInfo.description}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contacto"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#1E40AF] text-white font-medium rounded-lg hover:bg-[#1E3A8A] transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-blue-500/20"
                >
                  Afíliate Ahora
                  <ArrowRight className="h-5 w-5" />
                </Link>
                <Link
                  to="/acerca-de/historia"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 backdrop-blur-sm text-white font-medium rounded-lg hover:bg-white/20 transition-all duration-300 border border-white/20"
                >
                  Conoce Más
                </Link>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-6">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/15 transition-all duration-300 hover-lift"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="text-4xl lg:text-5xl font-bold text-white mb-2">
                    {stat.number}
                  </div>
                  <div className="text-blue-300 text-sm">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#1E40AF] text-sm font-semibold uppercase tracking-wider">Nuestros Valores</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0A1628] mt-3 mb-4">
              Principios que nos guían
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Los valores que definen nuestra identidad y compromiso con el empresariado mexicano
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {values.map((value, index) => (
              <div
                key={index}
                className="group bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-100 hover:border-[#1E40AF]/30 hover:shadow-lg transition-all duration-300 text-center"
              >
                <div className="w-12 h-12 mx-auto mb-4 bg-[#1E40AF]/10 rounded-xl flex items-center justify-center group-hover:bg-[#1E40AF] transition-colors">
                  <Heart className="h-6 w-6 text-[#1E40AF] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-sm font-medium text-[#0A1628]">{value}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-[#F8FAFC] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#1E40AF] text-sm font-semibold uppercase tracking-wider">¿Por qué afiliarte?</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#0A1628] mt-3 mb-6">
                Beneficios de ser parte de COPARMEX
              </h2>
              <p className="text-gray-600 mb-8">
                {companyInfo.slogan}
              </p>
              <Link
                to="/contacto"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#1E40AF] text-white font-medium rounded-lg hover:bg-[#1E3A8A] transition-all duration-300"
              >
                Conoce cómo afiliarte
                <ArrowRight className="h-5 w-5" />
              </Link>
            </div>

            <div className="grid gap-4">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md hover:border-[#1E40AF]/20 transition-all duration-300"
                >
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#1E40AF]/10 rounded-xl flex items-center justify-center">
                      {index === 0 && <Users className="h-6 w-6 text-[#1E40AF]" />}
                      {index === 1 && <Building2 className="h-6 w-6 text-[#1E40AF]" />}
                      {index === 2 && <Shield className="h-6 w-6 text-[#1E40AF]" />}
                      {index === 3 && <Lightbulb className="h-6 w-6 text-[#1E40AF]" />}
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#0A1628] mb-2">{benefit.title}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* News Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-12">
            <div>
              <span className="text-[#1E40AF] text-sm font-semibold uppercase tracking-wider">Mantente Informado</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#0A1628] mt-3">
                Últimas Noticias
              </h2>
            </div>
            <Link
              to="/noticias"
              className="inline-flex items-center gap-2 text-[#1E40AF] font-medium hover:gap-3 transition-all"
            >
              Ver todas las noticias
              <ChevronRight className="h-5 w-5" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {newsArticles.map((article) => (
              <Link
                key={article.id}
                to={`/noticias/${article.id}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 hover-lift"
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <span className="inline-block px-3 py-1 bg-[#1E40AF]/10 text-[#1E40AF] text-xs font-medium rounded-full mb-3">
                    {article.category}
                  </span>
                  <h3 className="font-semibold text-[#0A1628] mb-2 group-hover:text-[#1E40AF] transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-gray-600 text-sm line-clamp-2 mb-3">
                    {article.excerpt}
                  </p>
                  <span className="text-xs text-gray-400">{article.date}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Events Section */}
      <section className="py-20 bg-[#0A1628]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-blue-400 text-sm font-semibold uppercase tracking-wider">Agenda</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mt-3 mb-4">
              Próximos Eventos
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Participa en nuestros eventos de capacitación, networking y desarrollo empresarial
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {upcomingEvents.map((event) => (
              <div
                key={event.id}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/15 transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-16 h-16 bg-[#1E40AF] rounded-xl flex flex-col items-center justify-center">
                    <span className="text-2xl font-bold text-white">
                      {new Date(event.date).getDate()}
                    </span>
                    <span className="text-xs text-blue-200">
                      {new Date(event.date).toLocaleString('es', { month: 'short' }).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">{event.title}</h3>
                    <p className="text-gray-400 text-sm mb-2">{event.description}</p>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {event.time}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#1E40AF] to-[#1E3A8A] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-white rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            ¿Listo para formar parte de la comunidad empresarial más grande de México?
          </h2>
          <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
            Únete a más de 36,000 empresas que confían en COPARMEX para representar sus intereses.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contacto"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#1E40AF] font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg"
            >
              Contáctanos
              <ArrowRight className="h-5 w-5" />
            </Link>
            <a
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/20 text-white font-semibold rounded-lg hover:bg-white/30 transition-all duration-300 backdrop-blur-sm border border-white/20"
            >
              <Phone className="h-5 w-5" />
              {companyInfo.phone}
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
